import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
  Button,
  TextField,
  Link,
  Typography,
  IconButton,
  OutlinedInput,
  Container,
  InputLabel,
  InputAdornment,
  FormControl,
  FormHelperText,
  Box,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ForgotPasswordDialog from "../../../Components/Widgets/Dialogs/ForgotPasswordDialog";

const LoginRegisterBtn = styled(Button)(({ theme }) => ({
  margin: theme.spacing(2, 0),
  padding: "12px 14px",
  background: "#f05d32",
  borderRadius: "12px",
  border: "1px solid #f05d32",
  color: "#ffffff",
  "&:hover": {
    background: "#f05d32",
  },
}));

const MyTextField = styled(TextField)(({ theme }) => ({
  "& label.Mui-focused": {
    color: "#7d7d7d",
    borderRadius: "12px",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#7d7d7d",
    borderRadius: "12px",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#7d7d7d",
      borderRadius: "12px",
    },
    "&:hover fieldset": {
      borderColor: "#7d7d7d",
      borderRadius: "12px",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#7d7d7d",
      borderRadius: "12px",
    },
  },
}));

const PasswordField = styled(FormControl)(({ theme }) => ({
  marginTop: "16px",
  marginBottom: "8px",
  width: "100%",
  "& label.Mui-focused": {
    color: "#7d7d7d",
    borderRadius: "12px",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#7d7d7d",
    borderRadius: "12px",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#7d7d7d",
      borderRadius: "12px",
    },
    "&:hover fieldset": {
      borderColor: "#7d7d7d",
      borderRadius: "12px",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#7d7d7d",
      borderRadius: "12px",
    },
  },
}));

export default function LogIn(props) {
  const { component, dialog } = props;

  const [values, setValues] = React.useState({
    password: "",
    showPassword: false,
  });

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const [open, setOpen] = React.useState(false);

  const redirect = component.getPage().getQuery("redirect");

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: { xs: 1, sm: 2 },
      }}
    >
      <Box>
        <LoginRegisterBtn
          type="submit"
          fullWidth
          sx={{
            borderRadius: "4px",
            height: "40px",
            textTransform: "none",
            bgcolor: "transparent",
            border: "1px solid #dadce0",
            color: "#3c4043",
            px: 1,
            "&:hover": {
              background: "rgba(66,133,244,.04)",
              borderColor: "#d2e3fc",
            },
            "& i": {
              fontSize: "20px",
              color: "#3b5998",
            },
          }}
          onClick={() => component.loginFB()}
          startIcon={<i className="fa-brands fa-facebook-f" />}
        >
          {component.ucfirst("register-facebook-btn")}
        </LoginRegisterBtn>
        <Box
          id="google-sign-in-btn"
          type="submit"
          fullWidth
          variant="contained"
        >
          <i
            className="fa-brands fa-google"
            style={{ fontSize: "20px", marginRight: "10px" }}
          ></i>
          {component.ucfirst("sign-in-google-btn")}
        </Box>
      </Box>
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: "1px solid rgba(0,0,0,.1)",
          width: "100%",
          textAlign: "center",
          position: "relative",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            zIndex: 1,
            position: "absolute",
            top: "0px",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: "50px",
            color: "#7d7d7d",
            backgroundColor: "#fff",
            border: "1px solid #fff",
            fontSize: "18px",
            padding: "0px 5px",
            width: "30px",
            height: "30px",
          }}
        >
          {component.trans("or")}
        </Typography>
      </div>
      <form
        style={{ width: "100%", marginTop: 8 }}
        onSubmit={(e) => component.login(e)}
      >
        <MyTextField
          variant="outlined"
          margin="normal"
          fullWidth
          id="email"
          label={component.ucfirst("email-textfield")}
          name="email"
          autoComplete="email"
          helperText={component.getData("error.fields.auth.username")}
          value={component.getData("default.fields.auth.username", "")}
          onChange={(e) => {
            component.setData({
              "default.fields.auth.username": e.target.value,
            });
            component.deleteData("error.fields.auth.username");
          }}
          error={component.dataExists("error.fields.auth.username")}
        />
        <PasswordField
          variant="outlined"
          error={component.dataExists("error.fields.auth.password")}
        >
          <InputLabel>{component.ucfirst("password-textfield")}</InputLabel>
          <OutlinedInput
            type={values.showPassword ? "text" : "password"}
            value={component.getData("default.fields.auth.password", "")}
            onChange={(e) => {
              component.setData({
                "default.fields.auth.password": e.target.value,
              });
              component.deleteData("error.fields.auth.password");
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  disableRipple
                  sx={{
                    "&:hover, &:focus": { backgroundColor: "transparent" },
                  }}
                >
                  {values.showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            label={component.ucfirst("password-textfield")}
          />
          {component.dataExists("error.fields.auth.password") && (
            <FormHelperText error style={{ margin: "3px 14px 0" }}>
              {component.getData("error.fields.auth.password")}
            </FormHelperText>
          )}
        </PasswordField>

        <LoginRegisterBtn type="submit" fullWidth variant="contained">
          {component.ucfirst("sign-in-btn")}
        </LoginRegisterBtn>
        <Link
          variant="body2"
          component="button"
          underline="hover"
          type="button"
          onClick={() => setOpen(true)}
          style={{
            color: "#7d7d7d",
          }}
        >
          {component.ucfirst("forgot-password")}
        </Link>
      </form>
      {dialog && (
        <>
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid rgba(0,0,0,.1)",
              width: "100%",
              textAlign: "center",
            }}
          >
            <Typography variant="body1">
              {component.ucfirst("no-account-text")}
            </Typography>
          </div>

          <LoginRegisterBtn
            type="submit"
            fullWidth
            variant="contained"
            component={RouterLink}
            to={`/register` + (redirect ? `?redirect=${redirect}` : "")}
          >
            {component.ucfirst("register-btn")}
          </LoginRegisterBtn>
        </>
      )}
      <ForgotPasswordDialog
        {...{
          ...props,
          ...{
            open,
            setOpen: (boolean) => setOpen(boolean),
          },
        }}
      />
    </Container>
  );
}
